import React from 'react'

const RELOAD_KEY = 'chunkLoadReloadAt'
const RELOAD_COOLDOWN_MS = 60 * 1000

const shouldReload = () => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0
    return Date.now() - last >= RELOAD_COOLDOWN_MS
  } catch (error) {
    // sessionStorage unavailable (private mode etc.) — allow the reload
    return true
  }
}

const markReload = () => {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch (error) {
    // ignore — worst case we reload again
  }
}

/**
 * React.lazy wrapper that survives a new deploy: hashed chunk files get
 * replaced, so a session started on the old index.html can hit 404s when
 * lazy chunks load. Retries the import a couple of times (transient network
 * failures) and, as a last resort, reloads the page once (cooldown-guarded)
 * so the browser picks up the current index.html and its matching chunks.
 *
 * @param {() => Promise<{ default: React.ComponentType }>} importFactory
 * @param {number} [retries]
 */
export const lazyWithRetry = (importFactory, retries = 2) =>
  React.lazy(
    () =>
      new Promise((resolve, reject) => {
        const attempt = (remaining) => {
          importFactory()
            .then(resolve)
            .catch((error) => {
              if (remaining <= 0) {
                if (shouldReload()) {
                  markReload()
                  window.location.reload()
                  return
                }
                reject(error)
                return
              }
              setTimeout(() => attempt(remaining - 1), 1000)
            })
        }
        attempt(retries)
      })
  )
